import {
  envIsDev,
  envIsIntegration,
  envIsLocal,
  serverEnableBackendGrouping,
  serverEnableCoreWeaveObservability,
  serverEnableSemanticLegend,
  serverFlagDynamicBinSizing,
} from '../config';
import {RampIdType} from '../generated/graphql';
import {Viewer} from '../state/viewer/types';
import {getDatadogRum} from './datadog/datadogRum';
import {
  checkEntityRampFlag,
  useEntityRampFlag,
  useEntityRampFlagWithLoading,
  useOrgIdRampFlag,
  useOrgNameRampFlag,
  useOrgRampFlag,
  useOrgRampFlagWithLoading,
  useUserOrOrgRampFlag,
  useUserRampFlag,
  useUserRampFlagWithLoading,
} from './useRampFlag';

const DD_RUM = getDatadogRum();
/*
Docs for ramp feature flags:
https://www.notion.so/wandbai/Ramp-Feature-Flag-Documentation-da56991072df4c8d980cd36804f52ded
*/

/**
 * This is a little hacky but we're wanting to safely prevent special orgs from getting
 * force enrolled in full fidelity until we're ready.
 * 1. The entity name check won't always work because not all workspaces are under namespaces like NVIDIA
 * 2. The `userInfo.company` field is not always populated
 * 3. viewer orgs is pretty reliable, so between all three of these this should be a comprehensive block
 */
// https://weightsandbiases.slack.com/archives/C062UKHTAHG/p1727116018580919
const BLOCK_LIST = ['OPENAI', 'NVIDIA'];
export const getShouldBlockEntity = (
  entityName: string,
  viewer: Viewer | undefined
) => {
  // block special entities
  const isEntityNameBlocked = BLOCK_LIST.includes(entityName.toUpperCase());

  // block based on viewer org
  const isViewerOrgBlocked = viewer?.organizations?.some(org => {
    return BLOCK_LIST.includes(org.name.toUpperCase());
  });

  // block based on userInfo.company
  const company = viewer?.userInfo?.company ?? '';
  const isCompanyBlocked = BLOCK_LIST.includes(company.toUpperCase());

  return isEntityNameBlocked || isViewerOrgBlocked || isCompanyBlocked;
};

export const useRampFlagLineConstructionProfiling = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'line-construction-profiling',
    RampIdType.EntityName
  );
};

export const useRampFlagDisableFuzzySearch = (entityName: string) => {
  const rampFlag = useEntityRampFlag(
    entityName,
    'disable-fuzzy-search',
    RampIdType.EntityName
  );
  if (envIsLocal) {
    return true;
  }
  return rampFlag;
};

export const useRampFlagDisableQueryMerging = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'disable-query-merging',
    RampIdType.EntityName
  );
};

export const useRampFlagDisableUpdateHistoryKeysInfo = () => {
  return useUserOrOrgRampFlag(
    'disable-update-history-key-info-user',
    'disable-update-history-key-info-org'
  );
};

export const checkRampFlagEnableOPFSCache = async (
  entityName: string
): Promise<boolean> => {
  return await checkEntityRampFlag(entityName, 'enable-opfs-cache');
};

export const useRampFlagEnableOPFSCache = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'enable-opfs-cache',
    RampIdType.EntityName
  );
};

export const useDynamicBinSizing = (entityName: string) => {
  const isStatsigFlagEnabled = useStatsigFlagDynamicBinSizing(entityName);
  const isDeprecatedRampFlagEnabled =
    useRampFlagLowResolutionFullFidelityQueries(entityName);
  const isServerFlagEnabled = serverFlagDynamicBinSizing();

  const value =
    isStatsigFlagEnabled || isDeprecatedRampFlagEnabled || isServerFlagEnabled;
  DD_RUM.setGlobalContextProperty('dynamic_bin_sizing', value);
  return value;
};

export const useStatsigFlagDynamicBinSizing = (entityName: string) => {
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'dynamic_bin_sizing_user',
    'dynamic_bin_sizing_org'
  );

  const entityFlag = useEntityRampFlag(
    entityName,
    'dynamic_bin_sizing_entity',
    RampIdType.EntityName
  );

  const value = userOrOrgFlag || entityFlag;
  return value;
};

// TO BE DEPRECATED
export const useRampFlagLowResolutionFullFidelityQueries = (
  entityName: string
) => {
  // Check existing ramp flags (to be deprecated)
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'low-resolution-queries-user',
    'low-resolution-queries-org'
  );

  const entityFlag = useEntityRampFlag(
    entityName,
    'low-resolution-queries-entity',
    RampIdType.EntityName
  );

  const value = userOrOrgFlag || entityFlag;
  DD_RUM.setGlobalContextProperty('low_resolution_queries', value);
  return value;
};

export const useRampFlagNonMonotonicXAxis = (entityName: string) => {
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'non-monotonic-x-axis-user',
    'non-monotonic-x-axis-org'
  );
  const entityFlag = useEntityRampFlag(
    entityName,
    'non-monotonic-x-axis-entity',
    RampIdType.EntityName
  );

  return userOrOrgFlag || entityFlag;
};

// in W&B server yet
export const useRampFlagAccountSelector = () => {
  if (envIsLocal) {
    return false;
  }
  return true;
};

export const useRampFlagNoKeysets = () => {
  return useUserOrOrgRampFlag('no-keysets-user', 'no-keysets-org');
};

export const useRampFlagInferenceCatalog = (orgName: string) => {
  const ret = useOrgNameRampFlag(
    orgName,
    'inference-catalog',
    RampIdType.OrgName
  );

  // disabling for server
  if (envIsLocal) {
    return false;
  }

  const rampFlag = !ret.loading && ret.data;

  return rampFlag ?? false;
};

export const useRampProjectSpecificRoles = (orgName: string) => {
  // NB: do not copy this pattern, it is exactly the same as `return useOrgNameRampFlag()`
  const {data: orgProjectSpecificRolesFlag, loading} = useOrgNameRampFlag(
    orgName,
    'project-specific-roles',
    RampIdType.OrgName
  );
  return {data: orgProjectSpecificRolesFlag ?? false, loading};
};

// not all scenarios have an entity name - in those cases, it's
// fine to rely on the org for the flag check
export const useRampFlagMetricChangeNotifications = (
  orgName: string,
  entityName: string
) => {
  const entityEnabled = useEntityRampFlag(
    entityName,
    'run-metric-notifications-entity',
    RampIdType.EntityName
  );

  const orgEnabled = useOrgNameRampFlag(
    orgName,
    'run-metric-notifications',
    RampIdType.OrgName
  );
  if (orgEnabled.loading) {
    return {data: false, loading: true};
  }

  // We OR because the org flag is how we individually pick orgs to enable,
  // and the entity enable is for broadly allowing any paid entity.
  // Right now we don't see a need to disable orgs for this flag.
  return {data: entityEnabled || orgEnabled.data, loading: false};
};

export const useRampFlagStarredProjects = () => {
  return useUserRampFlag('starred-projects', RampIdType.UserName);
};

export const useRampFlagRunMetricZscore = (entityName: string) => {
  return useEntityRampFlagWithLoading(
    entityName,
    'run-metric-zscore-entity',
    RampIdType.EntityName
  );
};

export const useRampFlagHideSummaryHistograms = (entityName: string) => {
  return useEntityRampFlag(
    entityName,
    'hide-summary-histograms',
    RampIdType.EntityName
  );
};

export const useRampFlagGQLLongTermCache = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'gql-long-term-cache-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'gql-long-term-cache-entity',
    RampIdType.EntityName
  );
  return isUserEnabled || isEntityEnabled;
};

export const useRampFlagAccountSelectorNew = () => {
  return useUserRampFlag('account-selector-new', RampIdType.UserName);
};

export const useRampFlagOrgLevelServiceAccounts = (orgName: string) => {
  return useOrgNameRampFlag(
    orgName,
    'org-level-service-accounts',
    RampIdType.OrgName
  );
};

export const useRampFlagEmbeddedPanels = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'embedded-panels-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'embedded-panels-entity',
    RampIdType.EntityName
  );
  if (envIsLocal || envIsIntegration) {
    return false;
  }
  return isUserEnabled || isEntityEnabled;
};

export const useRampFlagRegistryObserverRole = (orgName: string) => {
  return useOrgNameRampFlag(
    orgName,
    'registry-observer-role',
    RampIdType.OrgName
  );
};

export const useRampFlagUseBackendGrouping = (entityName: string) => {
  const isOrgEnabled = useOrgRampFlag(
    'use-backend-grouping-org',
    RampIdType.OrgName
  );
  const isUserEnabled = useUserRampFlag(
    'use-backend-grouping-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'use-backend-grouping-entity',
    RampIdType.EntityName
  );
  if (envIsLocal) {
    return serverEnableBackendGrouping();
  }
  const value = isOrgEnabled || isUserEnabled || isEntityEnabled;
  DD_RUM.setGlobalContextProperty('use_backend_grouping', value);
  return value;
};

export const useRampFlagSemanticLegend = (entityName: string) => {
  const isOrgEnabled = useOrgRampFlag(
    'semantic-legend-org',
    RampIdType.OrgName
  );
  const isUserEnabled = useUserRampFlag(
    'semantic-legend-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'semantic-legend-entity',
    RampIdType.EntityName
  );
  if (envIsLocal) {
    return serverEnableSemanticLegend();
  }
  return isOrgEnabled || isUserEnabled || isEntityEnabled;
};

export const useRampFlagUseBackendSmoothing = (entityName: string) => {
  const isDeploymentEnabled = useOrgRampFlag(
    'backend_smoothing_deployment',
    RampIdType.OrgId
  );
  const isUserEnabled = useUserRampFlag(
    'backend_smoothing_user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'backend_smoothing_entity',
    RampIdType.EntityName
  );
  if (envIsLocal) {
    return isDeploymentEnabled ?? false;
  }
  const value = isUserEnabled || isEntityEnabled;
  DD_RUM.setGlobalContextProperty('backend_smoothing', value);
  return value;
};

export const useRampFlagTeamReadmeSection = () =>
  useUserRampFlag('team-readme-section', RampIdType.UserName);

export const useRampFlagOverlayWorkspaceDrawer = () => {
  const isUserEnabled = useUserRampFlag(
    'overlay-workspace-drawer-by-user',
    RampIdType.UserName
  );
  if (envIsLocal) {
    return true;
  }
  return isUserEnabled;
};

export const useRampFlagMetricsSearchAPI = (entityName: string) => {
  const userFlag = useUserRampFlag(
    'metrics-search-api-user',
    RampIdType.UserName
  );
  const entityFlag = useEntityRampFlag(
    entityName,
    'metrics-search-api-entity',
    RampIdType.EntityName
  );
  return userFlag || entityFlag;
};

export const useRampFlagIssuesObservability = (entityName: string) => {
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'issues-observability-user',
    'issues-observability-org'
  );
  const entityFlag = useEntityRampFlag(
    entityName,
    'issues-observability-entity',
    RampIdType.EntityName
  );

  if (envIsLocal) {
    return serverEnableCoreWeaveObservability();
  }
  const value = userOrOrgFlag || entityFlag;
  DD_RUM.setGlobalContextProperty('issues_observability', value);
  return value;
};

export const useStatsigIssuesObservability = (orgId: string) => {
  const value = useOrgIdRampFlag(
    'coreweave_observability',
    RampIdType.OrgId,
    orgId
  );

  DD_RUM.setGlobalContextProperty('coreweave_observability', value);
  return value;
};

export const useRampFlagModelRegistryMigrationNotification = (
  orgName: string
) => {
  const orgFlag = useOrgNameRampFlag(
    orgName,
    'model-registry-migration-notification',
    RampIdType.OrgName
  );
  if (envIsLocal) {
    return {data: false, loading: false};
  }
  return orgFlag;
};

export const useRampFlagMediaPanelTracking = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'media-panel-tracking-entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'media-panel-tracking-user',
    RampIdType.UserName
  );
  const isOrgEnabled = useOrgRampFlag(
    'media-panel-tracking-org',
    RampIdType.OrgName
  );
  return isEntityEnabled || isUserEnabled || isOrgEnabled;
};

export const useRampFlagMediaPanelTrackingDebug = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'media_panel_tracking_debug_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'media_panel_tracking_debug_user',
    RampIdType.UserName
  );
  return isEntityEnabled || isUserEnabled;
};

export const useRampFlagShowOrgActivityPage = () => {
  const isOrgEnabled = useOrgRampFlag(
    'show_org_activity_page',
    RampIdType.OrgId
  );

  return isOrgEnabled || false;
};

export const useStatsigGateCoreWeaveSaasByob = (
  organizationId?: string | null
) => {
  // Only used for Saas as setting up CoreWeave buckets in server is a different process
  const isOrgEnabled = useOrgIdRampFlag(
    'saas_coreweave_storage_byob',
    RampIdType.OrgId,
    organizationId
  );

  if (envIsLocal) {
    return false;
  }
  return isOrgEnabled;
};

export const useRampInferenceAsProduct = () => {
  return useOrgRampFlag('inference_as_product', RampIdType.OrgName);
};

export const useRampProPlanMonthlyOption = () => {
  const isUserEnabled = useUserRampFlag(
    'pro_plan_monthly_option',
    RampIdType.UserId
  );

  // never show for dedicated
  if (envIsLocal) {
    return false;
  }

  return isUserEnabled;
};

export const useStatsigGateMultiMetricQuickAdd = () => {
  return useUserRampFlag('multi_metric_quick_add', RampIdType.UserId) ?? false;
};

export const useRampFlagFullScreenLinePlotEditing = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'fullscreen_line_plot_editing_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'fullscreen_line_plot_editing_user',
    RampIdType.UserName
  );
  if (envIsIntegration || envIsLocal) {
    return false;
  }
  return isEntityEnabled || isUserEnabled;
};

export const useRampFlagBRSDQWithoutRunInfo = (entityName: string) => {
  const {isEnabled: isUserEnabled, loading: isUserEnabledLoading} =
    useUserRampFlagWithLoading(
      'brsdq_without_run_info_user',
      RampIdType.UserId
    );
  const {data: isEntityEnabled, loading: isEntityEnabledLoading} =
    useEntityRampFlagWithLoading(
      entityName,
      'brsdq_without_run_info_entity',
      RampIdType.EntityId
    );
  const {data: isOrgEnabled, loading: isOrgEnabledLoading} =
    useOrgRampFlagWithLoading('brsdq_without_run_info_org', RampIdType.OrgId);
  if (isUserEnabledLoading || isEntityEnabledLoading || isOrgEnabledLoading) {
    return {
      isEnabled: undefined,
      loading: true,
    };
  }
  const definedFlags = [isUserEnabled, isEntityEnabled, isOrgEnabled].filter(
    flag => flag !== undefined
  );
  return {
    isEnabled: definedFlags.some(flag => flag),
    loading: false,
  };
};

export const useStatsigGateSourceArtifactProtection = (
  organizationName: string
): {data: boolean; loading: boolean} => {
  const {data, loading} = useOrgNameRampFlag(
    organizationName,
    'source_artifact_protection',
    RampIdType.OrgName
  );

  return {data, loading};
};

export const useStatsigGateAutomationExecutionHistory = (
  organizationId?: string | null
) => {
  return useOrgIdRampFlag(
    'automation_execution_history',
    RampIdType.OrgId,
    organizationId
  );
};

/**
 * This currently doesn't support an entity flag because I didn't want to do the work to thread and entity value into the check. It probably won't be that hard but we're just experimenting currently.
 */
export const useRampFlagFullFidelityRunsPerChart = () => {
  const userOrOrg = useUserOrOrgRampFlag(
    'full_fidelity_run_per_chart_user',
    'full_fidelity_run_per_chart_org'
  );
  DD_RUM.setGlobalContextProperty('full_fidelity_run_per_chart', userOrOrg);
  return userOrOrg;
};

export const useRampFlagWeaveAnnotationQueue = () => {
  return useOrgIdRampFlag('weave_annotation_queue', RampIdType.OrgId);
};

export const useRampFlagFullFidelityRanges = (entityName: string) => {
  const isDeploymentEnabled = useOrgRampFlag(
    'full_fidelity_ranges_deployment',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'full_fidelity_ranges_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'full_fidelity_ranges_user',
    RampIdType.UserName
  );
  if (envIsLocal) {
    return isDeploymentEnabled ?? true;
  }
  return isEntityEnabled || isUserEnabled;
};

export const useStatsigGateBulkUserInvitations = () => {
  return useOrgIdRampFlag('bulk_org_invitations', RampIdType.OrgId);
};

export const useRampFlagOrgAlertsTab = () => {
  return useOrgRampFlag('org_alerts_tab', RampIdType.OrgId) ?? false;
};

export const useStatsigGateBackendLineConstruction = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'backend_line_construction_user',
    RampIdType.UserId
  );
  const isOrgEnabled = useOrgRampFlag(
    'backend_line_construction_org',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'backend_line_construction_entity',
    RampIdType.EntityId
  );
  const value = isUserEnabled || isOrgEnabled || isEntityEnabled;
  DD_RUM.setGlobalContextProperty('backend_line_construction_user', value);
  return value;
};

export const useStatsigGateBackendLineConstructionSmoothing = (
  entityName: string
) => {
  const value = useEntityRampFlag(
    entityName,
    'backend_line_construction_smoothing',
    RampIdType.EntityName
  );
  DD_RUM.setGlobalContextProperty('backend_line_construction_smoothing', value);
  return value;
};

export const useStatsigGateBackendLineConstructionTimeUnits = (
  entityName: string
) => {
  const value = useEntityRampFlag(
    entityName,
    'backend_line_construction_time_units',
    RampIdType.EntityName
  );
  DD_RUM.setGlobalContextProperty(
    'backend_line_construction_time_units',
    value
  );
  return value;
};

export const useStatsigGateSemanticLegendsBucketEditing = () => {
  return useOrgRampFlag(
    'semantic_legend_bucket_editing_org',
    RampIdType.OrgName
  );
};

export const useRampFlagDisableCopyApiKey = () => {
  return useUserRampFlag('disablecopyapikey', RampIdType.UserId);
};

export const useRampFlagDisableCopyApiKeyWithLoading = () => {
  return useUserRampFlagWithLoading('disablecopyapikey', RampIdType.UserId);
};

export const useRampFlagPinnedRuns = () => {
  const flagEnabled = useUserRampFlag('pinned_runs', RampIdType.UserId);
  return envIsDev || envIsIntegration || flagEnabled;
};

export const useRampFlagBaselineRuns = () => {
  const baselineEnabled = useUserRampFlag('baseline_runs', RampIdType.UserId);
  const pinnedRunsEnabled = useRampFlagPinnedRuns();
  // Baseline runs depends on pinned runs being enabled
  return envIsDev || envIsIntegration || (pinnedRunsEnabled && baselineEnabled);
};

export const useRampFlagBaselineDeltaMetrics = () => {
  const deltaMetricsEnabled = useUserRampFlag(
    'baseline_delta_metrics',
    RampIdType.UserId
  );

  return envIsDev || envIsIntegration || deltaMetricsEnabled;
};

export const useStatsigGateRegistryHomepageAdjustmentsWithLoading = (
  orgId: string
): {enabled: boolean; loading: boolean} => {
  const ret = useOrgRampFlagWithLoading(
    'registry_homepage_adjustments',
    RampIdType.OrgId,
    orgId
  );

  return {
    enabled: (!ret.loading && ret.data) || false,
    loading: ret.loading,
  };
};

export const useStatsigGateRegistryHomepageAdjustments = (orgId: string) => {
  const {enabled} = useStatsigGateRegistryHomepageAdjustmentsWithLoading(orgId);
  return enabled;
};

export const useRampFlagRunLogsPagination = () => {
  return useUserRampFlag('run_logs_pagination', RampIdType.UserId);
};

export const useRampFlagMediaPanelLayoutRefactor = () => {
  return useUserRampFlag('media_panel_layout_refactor', RampIdType.UserId);
};

export const useRampFlagMediaPanelCompareMode = (entityName: string) => {
  // compare mode solely based on new layout
  const isNewLayoutEnabled = useRampFlagMediaPanelLayoutRefactor();
  const isUserEnabled = useUserRampFlag(
    'media_panel_compare_mode_user',
    RampIdType.UserId
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'media_panel_compare_mode_entity',
    RampIdType.EntityId
  );
  const isOrgEnabled = useOrgRampFlag(
    'media_panel_compare_mode_org',
    RampIdType.OrgId
  );
  return (
    isNewLayoutEnabled && (isUserEnabled || isEntityEnabled || isOrgEnabled)
  );
};

export const useRampFlagVideoStreaming = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'video_streaming_entity',
    RampIdType.EntityId
  );
  return isEntityEnabled;
};

export const useStatsigGateRegistryUIPhase2 = (orgId: string) => {
  const isOrgEnabled = useOrgIdRampFlag(
    'registry_ui_phase_2',
    RampIdType.OrgId,
    orgId
  );

  return isOrgEnabled || false;
};

export const useStatsigGateRegistryUIPhase3 = (orgId: string) => {
  const isOrgEnabled = useOrgIdRampFlag(
    'registry_ui_phase_3',
    RampIdType.OrgId,
    orgId
  );

  return isOrgEnabled || false;
};

export const useRampFlagEntityLessProfilePage = () => {
  return useUserRampFlag('entity_less_profile_page', RampIdType.UserId);
};

export const useRampFlagUPlotUser = () => {
  return useUserRampFlag('uplot_user', RampIdType.UserId);
};

export const useRampFlagLinePlotProfiler = () => {
  return useUserRampFlag('line_plot_profiler', RampIdType.UserId);
};

export const useRampFlagUnlimitedTrackedHoursForAll = () => {
  return useUserRampFlag('unlimited_tracked_hours_for_all', RampIdType.UserId);
};

export const useStatsigGateCombinedMetricsRunsLimitUI = (
  entityName: string
) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'combined_metrics_runs_limit_ui',
    RampIdType.EntityId
  );

  if (envIsIntegration) {
    return false;
  }

  const value = isEntityEnabled;
  DD_RUM.setGlobalContextProperty('combined_metrics_runs_limit_ui', value);
  return value;
};

export const useStatsigGateLinkWeaveToRegistry = () => {
  return useOrgRampFlag('link_weave_prompts_to_registry', RampIdType.OrgId);
};
